import React from 'react'
import debounce from 'lodash/debounce'
import sortBy from 'lodash/sortBy'
import SoundSearchProviderContainer from '../../../../../containers/search/SoundSearchProviderContainer'
import { useSoundSearchContext } from '../../../../../services/search/SoundSearchContext'
import EditorSoundLibraryResultSortHeaders from './EditorSoundLibraryResultSortHeaders'
import EditorSoundLibrarySoundClipRow from './EditorSoundLibrarySoundClipRow'
import Icon from '../../../../misc/Icons'
import analytics from '../../../../../utils/cdpAnalytics'
import { conformDeliveryUrl } from '../../../../../utils/mediaDeliveryUrls'

// Category names the API resolves server-side. A search runs across the
// requester's own clips and everything published, reported as two groups; the
// browse categories are the rows in the nav below.
const SEARCH_CATEGORIES = ['yourSounds', 'discover']

export function getSortedHitsData(sortType, sortOrder, hits) {
  let sorted = hits
  if (sortType === 'duration') {
    sorted = sortBy(sorted, [(hit) => hit.end - hit.start])
  } else if (sortType === 'name') {
    sorted = sortBy(sorted, [(hit) => hit.title.toString().toLowerCase()])
  }
  if (sortOrder !== 'ascending') {
    sorted = sorted.slice().reverse()
  }
  return sorted
}

function SearchGroupLoadingIndicator() {
  const { isSearching } = useSoundSearchContext()
  return isSearching ?
    <div className='editor-insertSoundModal--soundLibrary-main-resultsList-searchGroupLoading editor-insertSoundModal--soundLibrary-main-resultsList-searchGroupLoading--loading' /> :
    <div className='editor-insertSoundModal--soundLibrary-main-resultsList-searchGroupLoading editor-insertSoundModal--soundLibrary-main-resultsList-searchGroupLoading--notLoading' />
}

function SearchBar({ searchTerm, handleOnInputChange }) {
  const { isSearching } = useSoundSearchContext()
  const isEmpty = searchTerm === ''
  const showSearchLoading = isSearching && !isEmpty
  return (
    <div className={`editor-insertSoundModal--soundLibrary-main-titleBar-searchBar ${isEmpty ? ' editor-insertSoundModal--soundLibrary-main-titleBar-searchBar--empty' : ' editor-insertSoundModal--soundLibrary-main-titleBar-searchBar--notEmpty'}`}>
      <input
        id='soundLibrarySearchInput'
        placeholder='Search Sound Library'
        autoFocus
        autoComplete='off'
        autoCorrect='off'
        autoCapitalize='off'
        className={`editor-insertSoundModal--soundLibrary-main-titleBar-searchBar-input ${isEmpty ? 'editor-insertSoundModal--soundLibrary-main-titleBar-searchBar-input--empty' : 'editor-insertSoundModal--soundLibrary-main-titleBar-searchBar-input--nonEmpty'}`}
        value={searchTerm}
        onChange={(e) => {
          handleOnInputChange(e.target.value)
        }}
      />
      <div className='editor-insertSoundModal--soundLibrary-main-titleBar-searchBar-iconContainer'>
        {!showSearchLoading &&
        <Icon name='magnifying-glass' />}
        {showSearchLoading && (
        <div className='editor-insertSoundModal--soundLibrary-main-titleBar-searchBar-iconContainer-loadingSpinnerContainer'>
          <div className='editor-insertSoundModal--soundLibrary-main-titleBar-searchBar-iconContainer-loadingSpinner' />
        </div>
        )}
      </div>
      {!isEmpty && (
      <button className='editor-insertSoundModal--soundLibrary-main-titleBar-searchBar-clearBtnContainer' onClick={() => { handleOnInputChange('') }}>
        <div className='editor-insertSoundModal--soundLibrary-main-titleBar-searchBar-clearBtn'>
          Cancel
          {/* <Icon name='cross' /> */}
        </div>
      </button>
      )}
    </div>
  )
}

function SearchResults({
  dataCategory,
  label,
  insertSoundFromSoundLibrary,
  hasSearchTerm,
  sortOrder,
  sortType,
  activeRowIndex,
  setActiveRow,
  playback,
  playItem,
  pauseItem,
  editSoundFromSoundLibrary,
  isLoading,
}) {
  const { getResults } = useSoundSearchContext()
  const { hits } = getResults(dataCategory)
  // Titleless clips used to be filtered out here, after they had already been
  // counted. The API no longer returns them.
  const sortedHits = hasSearchTerm ?
    hits :
    getSortedHitsData(sortType, sortOrder, hits)
  return (
    <React.Fragment>
      <SearchGroupLoadingIndicator />
      <div className={`editor-insertSoundModal--soundLibrary-main-resultsList-searchGroup ${(hasSearchTerm && sortedHits.length === 0) ? ' editor-insertSoundModal--soundLibrary-main-resultsList-searchGroup--noResults' : ''}`}>
        {hasSearchTerm && !isLoading && (
        <div className='editor-insertSoundModal--soundLibrary-main-resultsList-searchGroup-groupTitle'>
          {label}
          {sortedHits.length === 0 && (
          <span className='editor-insertSoundModal--soundLibrary-main-resultsList-searchGroup-groupTitle-noResults'>
            (No Results)
          </span>
          )}
          {sortedHits.length > 0 && (
          <span className='editor-insertSoundModal--soundLibrary-main-resultsList-searchGroup-groupTitle-noResults'>
            (
            {sortedHits.length}
            {' '}
            Results)
          </span>
          )}
        </div>
        )}
        {sortedHits.length !== 0 && sortedHits.map((item, index) => (
          <EditorSoundLibrarySoundClipRow
            active={`${label}_${index}` === activeRowIndex}
            /* eslint-disable react/no-array-index-key */
            key={`${conformDeliveryUrl(item.deliveryUrl)}_${index}`}
            /* eslint-enable react/no-array-index-key */
            soundClipName={item.title}
            duration={item.fullDuration}
            start={item.start}
            end={item.end}
            deliveryUrl={conformDeliveryUrl(item.deliveryUrl)}
            setActiveRow={() => { setActiveRow(`${label}_${index}`, item) }}
            playback={playback}
            playItem={() => { playItem(`${label}_${index}`) }}
            pauseItem={() => { pauseItem(`${label}_${index}`) }}
            rowIndex={`${label}_${index}`}
            editSoundFromSoundLibrary={() => { editSoundFromSoundLibrary(item) }}
            insertSoundFromSoundLibrary={() => { insertSoundFromSoundLibrary(item) }}
          />
        ))}
      </div>
    </React.Fragment>
  )
}

function EditorSoundLibraryCategoryNavCategory({
  category, categoryName, active, selectCategory,
}) {
  const { counts } = useSoundSearchContext()
  // Counts arrive in one request. Until they do the slot is left blank; it used
  // to show the unfiltered total for the whole index, which was never the
  // number this row stands for.
  const resultsCount = counts[category]

  return (
    <button onClick={selectCategory} className={`editor-insertSoundModal--soundLibrary-categoryNav--category ${active ? 'editor-insertSoundModal--soundLibrary-categoryNav--category--active' : 'editor-insertSoundModal--soundLibrary-categoryNav--category--inactive'}`}>
      <div className='editor-insertSoundModal--soundLibrary-categoryNav--category-name'>
        {categoryName}
      </div>
      {category && (
      <div className='editor-insertSoundModal--soundLibrary-categoryNav--category-resultsCount'>
        {resultsCount !== undefined && resultsCount}
      </div>
      )}
    </button>
  )
}

const DEBOUNCE_TIME = 1000

class EditorSoundLibraryPage extends React.Component {
  constructor() {
    super()
    this.handleKeyDown = this.handleKeyDown.bind(this)
    this.setSortType = this.setSortType.bind(this)
    this.handleOnInputChange = this.handleOnInputChange.bind(this)
    this.selectCategory = this.selectCategory.bind(this)
    this.setActiveRow = this.setActiveRow.bind(this)

    this.sendSoundLibrarySearchTermAnalytics = debounce(
      this.sendSoundLibrarySearchTermAnalytics,
      DEBOUNCE_TIME,
      { leading: false },
    )

    this.state = {
      searchTerm: '',
      category: 'yourSounds',
      sortType: 'name',
      sortOrder: 'ascending',
      activeRowIndex: null,
      activeSound: null,
      activeSoundBrowseSoundLibraryCategory: null,
      activeSoundSearchSoundLibraryCategory: null,
      playback: { // sound currently being played in sound library
        playbackRow: null,
        isPlaying: false,
      },
    }
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown)
    analytics.page('Sound Library')
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown)
  }

  selectCategory(category) {
    if (category !== this.state.category) {
      this.setState({
        category,
        activeRowIndex: null,
        activeSound: null,
        playback: {
          playbackRow: null,
          isPlaying: false,
        },
      })
    }
  }

  handleKeyDown(e) {
    const {
      activeRowIndex,
      playback,
    } = this.state
    if (e.keyCode === 27) { // escape key
      this.props.closeInsertSoundModal()
    } else if (e.keyCode === 32) { // spacebar
      const searchInputFocused = document.getElementById('soundLibrarySearchInput') === document.activeElement
      if (!searchInputFocused) { // don't do space bar if focused on search bar
        e.preventDefault()
        if (activeRowIndex) {
          const isPlaying = playback && playback.playbackRow === activeRowIndex ?
            !playback.isPlaying :
            true
          this.setState({
            playback: {
              playbackRow: activeRowIndex,
              isPlaying,
            },
          })
        }
      }
    }
  }

  setSortType(type) {
    const { sortType, sortOrder } = this.state
    let newSortOrder = 'ascending'
    if (type === sortType) {
      if (sortOrder === 'ascending') {
        newSortOrder = 'descending'
      } else {
        newSortOrder = 'ascending'
      }
    }
    this.setState({
      sortType: type,
      sortOrder: newSortOrder,
      activeRowIndex: null,
      activeSound: null,
      playback: {
        playbackRow: null,
        isPlaying: false,
      },
    })
  }

  sendSoundLibrarySearchTermAnalytics(searchTerm) {
    analytics.track('Sound library search', {
      searchTerm,
    })
  }

  handleOnInputChange(newValue) {
    this.sendSoundLibrarySearchTermAnalytics(newValue)
    this.setState({
      searchTerm: newValue,
      activeRowIndex: null,
      playback: {
        playbackRow: null,
        isPlaying: false,
      },
    })
  }

  setActiveRow(index, sound, browseSoundLibraryCategory, searchSoundLibraryCategory) {
    if (this.state.activeRowIndex !== index) {
      this.setState({
        activeRowIndex: index,
        activeSound: sound,
        activeSoundBrowseSoundLibraryCategory: browseSoundLibraryCategory,
        activeSoundSearchSoundLibraryCategory: searchSoundLibraryCategory,
      })
    } else {
      this.setState({
        activeRowIndex: null,
        activeSound: null,
        activeSoundBrowseSoundLibraryCategory: browseSoundLibraryCategory,
        activeSoundSearchSoundLibraryCategory: searchSoundLibraryCategory,
      })
    }
  }

  render() {
    const { category, searchTerm } = this.state
    const hasSearchTerm = !!searchTerm

    // Browsing shows one category; searching reports the requester's own clips
    // and everything published as two groups.
    const activeCategories = hasSearchTerm ? SEARCH_CATEGORIES : [category]

    // Category name for title
    let selectedCategoryName = 'Search Results'
    let isDiscover = false

    if (category === 'yourSounds' && !hasSearchTerm) {
      selectedCategoryName = 'Your Sounds'
    }
    // I'm not sure if we need nicely formatted names for categoryies
    if (category !== 'yourSounds' && !hasSearchTerm) {
      selectedCategoryName = category
      isDiscover = true
    }

    const resultsListCategoryType = 'personal' // your sounds
    // const resultsListCategoryType = 'public' // discover content
    // const resultsListCategoryType = 'mixed' // "everything"

    const showInsertButton = (this.state.activeRowIndex || this.state.activeRowIndex === 0)
    return (
      <React.Fragment>
        <div className='editor-insertSoundModal editor-insertSoundModal--soundLibrary'>
          <SoundSearchProviderContainer categories={activeCategories} query={searchTerm}>
            <div className='editor-insertSoundModal--soundLibrary-categoryNav'>
              {hasSearchTerm &&
              <EditorSoundLibraryCategoryNavCategory active categoryName='Search Results' />}
              {!hasSearchTerm && (
              <React.Fragment>
                <EditorSoundLibraryCategoryNavCategory selectCategory={() => { this.selectCategory('yourSounds') }} active={category === 'yourSounds'} category='yourSounds' categoryName='Your Sounds' />
                <div className='editor-insertSoundModal--soundLibrary-categoryNav-discoverLabel'>
                  Discover
                </div>
                <EditorSoundLibraryCategoryNavCategory selectCategory={() => { this.selectCategory('EFL') }} active={category === 'EFL'} category='EFL' categoryName='EFL' />
                <EditorSoundLibraryCategoryNavCategory selectCategory={() => { this.selectCategory('french') }} active={category === 'french'} category='french' categoryName='French' />
                <EditorSoundLibraryCategoryNavCategory selectCategory={() => { this.selectCategory('music') }} active={category === 'music'} category='music' categoryName='Music' />
                <EditorSoundLibraryCategoryNavCategory selectCategory={() => { this.selectCategory('science') }} active={category === 'science'} category='science' categoryName='Science' />
                <EditorSoundLibraryCategoryNavCategory selectCategory={() => { this.selectCategory('spanish') }} active={category === 'spanish'} category='spanish' categoryName='Spanish' />
              </React.Fragment>
              )}
              {/* ADD LATER
                <button className='editor-insertSoundModal--soundLibrary-categoryNav-addSubject'>
                  Add Subject
                </button>
              */}

              <div className='editor-insertSoundModal--soundLibrary-categoryNav-vSpacer' />

              <a target='_blank' className='editor-insertSoundModal--soundLibrary-categoryNav-helpBtn' rel='noopener noreferrer' href='https://help.plickers.com/hc/en-us/articles/4405741980059'>
                Sound Library Help
              </a>

              <div className='editor-insertSoundModal--soundLibrary-categoryNav-submitBtnContainer'>
                {showInsertButton && (
                <button onClick={() => { this.props.insertSoundFromSoundLibrary(this.state.activeSound, this.state.activeSoundBrowseSoundLibraryCategory, this.state.activeSoundSearchSoundLibraryCategory) }} className='editor-insertSoundModal--soundLibrary-categoryNav-insertBtn'>
                  <div className='editor-insertSoundModal--soundLibrary-categoryNav-insertBtn-primary'>
                    Insert Sound Clip
                  </div>
                  <div className='editor-insertSoundModal--soundLibrary-categoryNav-insertBtn-secondary'>
                    Enter
                  </div>
                </button>
                )}
                <button className='editor-insertSoundModal--soundLibrary-categoryNav-cancelBtn' onClick={this.props.closeModal}>
                  Cancel
                </button>
              </div>
            </div>
            <div className='editor-insertSoundModal--soundLibrary-main'>
              <div className={`editor-insertSoundModal--soundLibrary-main-titleBar ${hasSearchTerm ? 'editor-insertSoundModal--soundLibrary-main-titleBar--search' : 'editor-insertSoundModal--soundLibrary-main-titleBar--browse'}`}>
                {!hasSearchTerm && (
                <React.Fragment>
                  <div className='editor-insertSoundModal--soundLibrary-main-titleBar-categoryNameContainer'>
                    <div className='editor-insertSoundModal--soundLibrary-main-titleBar-categoryName'>
                      {isDiscover && (
                      <div className='editor-insertSoundModal--soundLibrary-main-titleBar-categoryName-discover'>
                        Discover
                      </div>
                      )}
                      <div className='editor-insertSoundModal--soundLibrary-main-titleBar-categoryName-categoryName'>
                        {selectedCategoryName}
                      </div>
                    </div>
                    {isDiscover && selectedCategoryName && (
                    <div className='editor-insertSoundModal--soundLibrary-main-titleBar-categoryLabel'>
                      Browse and add sounds from published
                      {' '}
                      <span>{selectedCategoryName}</span>
                      {' '}
                      packs
                    </div>
                    )}
                  </div>
                </React.Fragment>
                )}
                <div className={`editor-insertSoundModal--soundLibrary-main-titleBar-searchBarContainer ${hasSearchTerm ? 'editor-insertSoundModal--soundLibrary-main-titleBar-searchBarContainer--active' : 'editor-insertSoundModal--soundLibrary-main-titleBar-searchBarContainer--inactive'}`}>
                  <SearchBar searchTerm={searchTerm} handleOnInputChange={this.handleOnInputChange} />
                </div>
              </div>
              <EditorSoundLibraryResultSortHeaders
                resultsListCategoryType={resultsListCategoryType}
                sortType={this.state.sortType}
                sortOrder={this.state.sortOrder}
                setSortType={this.setSortType}
                hasSearchTerm={hasSearchTerm}
              />

              {!hasSearchTerm && (
              <div className='editor-insertSoundModal--soundLibrary-main-resultsList editor-insertSoundModal--soundLibrary-main-resultsList--browse'>
                <SearchResults
                  dataCategory={category}
                  label='Browse'
                  hasSearchTerm={hasSearchTerm}
                  sortType={this.state.sortType}
                  sortOrder={this.state.sortOrder}
                  activeRowIndex={this.state.activeRowIndex}
                  setActiveRow={(index, sound) => {
                    this.setActiveRow(index, sound, this.state.category, null)
                  }}
                  playback={this.state.playback}
                  playItem={(index) => {
                    this.setState({ playback: { playbackRow: index, isPlaying: true } })
                  }}
                  pauseItem={(index) => {
                    this.setState({ playback: { playbackRow: index, isPlaying: false } })
                  }}
                  editSoundFromSoundLibrary={(sound) => {
                    this.props.editSoundFromSoundLibrary(sound)
                  }}
                  insertSoundFromSoundLibrary={(sound) => {
                    this.props.insertSoundFromSoundLibrary(sound, this.state.category, null)
                  }}
                />
              </div>
              )}
              {hasSearchTerm && (
              <div className='editor-insertSoundModal--soundLibrary-main-resultsList editor-insertSoundModal--soundLibrary-main-resultsList--search'>
                <SearchResults
                  dataCategory='yourSounds'
                  label='Your Sounds'
                  hasSearchTerm={hasSearchTerm}
                  sortType={this.state.sortType}
                  sortOrder={this.state.sortOrder}
                  activeRowIndex={this.state.activeRowIndex}
                  setActiveRow={(index, sound) => { this.setActiveRow(index, sound, null, 'yourSounds') }}
                  playback={this.state.playback}
                  playItem={(index) => {
                    this.setState({ playback: { playbackRow: index, isPlaying: true } })
                  }}
                  pauseItem={(index) => {
                    this.setState({ playback: { playbackRow: index, isPlaying: false } })
                  }}
                  editSoundFromSoundLibrary={(sound) => {
                    this.props.editSoundFromSoundLibrary(sound)
                  }}
                  insertSoundFromSoundLibrary={(sound) => {
                    this.props.insertSoundFromSoundLibrary(sound, null, 'yourSounds')
                  }}
                />

                <SearchResults
                  dataCategory='discover'
                  label='Discover'
                  hasSearchTerm={hasSearchTerm}
                  sortType={this.state.sortType}
                  sortOrder={this.state.sortOrder}
                  activeRowIndex={this.state.activeRowIndex}
                  setActiveRow={(index, sound) => { this.setActiveRow(index, sound, null, 'discover') }}
                  playback={this.state.playback}
                  playItem={(index) => {
                    this.setState({ playback: { playbackRow: index, isPlaying: true } })
                  }}
                  pauseItem={(index) => {
                    this.setState({ playback: { playbackRow: index, isPlaying: false } })
                  }}
                  editSoundFromSoundLibrary={(sound) => {
                    this.props.editSoundFromSoundLibrary(sound)
                  }}
                  insertSoundFromSoundLibrary={(sound) => {
                    this.props.insertSoundFromSoundLibrary(sound, null, 'discover')
                  }}
                />
              </div>
              )}
            </div>
          </SoundSearchProviderContainer>
        </div>
        <div onClick={this.props.closeModal} className='editor-insertSoundModal--soundLibraryBG' />
      </React.Fragment>
    )
  }
}

export default EditorSoundLibraryPage
